import * as THREE from 'three';
import CommandIdEnum from '../enums/CommandIdEnums';
import CanvasInputListenerModeEnum from '../enums/CanvasInputListenerModeEnum';
import MoveGizmoPartEnum from '../enums/MoveGizmoPartEnum';
import { emitter } from '../input/AppEvents';
import IParameterizable from '../interfaces/IParameterizable';
import CommandStack from '../logic/CommandStack';
import ICanvasInputListener from '../logic/ICanvasInputListener';
import RaycastIntersection from '../logic/RaycastIntersection';
import Document from '../model/document/Document';
import { SceneManager } from '../model/SceneManager';
import ISceneObject from '../model/sceneObject/ISceneObject';
import { getSceneObjectStyle, SceneObjectStyleEnum } from '../model/sceneObject/SceneObjectStyle';
import SceneObjectData from '../model/sceneObjectData/SceneObjectData';
import { useCurrentCommandState } from '../store/currentCommandStateStore';
import { tolerances } from '../utils/constants';
import { formatCm } from '../utils/dragMeasurement';
import { UUID } from '../utils/typeDefinitions';
import GeometryCommand from './GeometryCommand';
import MoveGizmoHelper from './commandHelpers/MoveGizmoHelper';
import { constrainDeltaToAxis, translationMatrix, worldDeltaToLocal } from './moveDelta';

// One object taking part in the move: the object in the document, the data it had before the
// current drag started, its parent's transform (for turning a world delta into local coordinates),
// and the styled clone that previews the drag while the original stays hidden and unmutated.
interface MoveTarget {
    sceneObject: ISceneObject;
    baselineData: SceneObjectData;
    parentWorldMatrix: THREE.Matrix4;
    preview?: ISceneObject;
}

// Moves whole scene objects with a translate gizmo: three axis arrows and three plane quads pinned
// to the selection's bounding-box centre. Clicking a part starts a constrained move, the next click
// commits it, and the gizmo stays up afterwards so several moves can be chained. The command ends
// on the running-command X button or on a click anywhere off the gizmo.
//
// Deliberately not a GeometryEditCmd: those carry exactly one EditHandleData for one owner and end
// after a single commit, whereas this drives N objects from a gizmo that outlives each move. It
// reuses their maths (see moveDelta.ts) rather than their class hierarchy, and takes a UUID list
// throughout so multi-select works the day it exists.
export default class MoveSceneObjectsCmd extends GeometryCommand implements IParameterizable<UUID[]> {

    #targets: MoveTarget[] = [];
    #gizmo: MoveGizmoHelper | undefined;
    // World view direction the current part visibility was decided for, so #onCameraRest can tell a
    // real reorientation from an ordinary pan or zoom.
    #gizmoViewDirection: THREE.Vector3 | undefined;

    // Set only while a drag is in progress; undefined means idle.
    #dragPart: MoveGizmoPartEnum | undefined;
    #dragStartOrigin: THREE.Vector3 | undefined;
    #dragAnchor: THREE.Vector3 | undefined;

    constructor(commandStack: CommandStack,
                canvasInputListener: ICanvasInputListener,
                document: Document) {
        super(CommandIdEnum.MoveSceneObjects, commandStack, canvasInputListener, document);
        this.commandNameResourceKey = 'command.moveSceneObjects.title';
        this.initialPrompResourceKey = 'command.moveSceneObjects.prompt';
    }

    setParameters(uuids: UUID[]): void {
        const document = this.getDocument();

        this.#targets = uuids
            .map(uuid => document.getSceneObject(uuid))
            .filter((sceneObject): sceneObject is ISceneObject => sceneObject !== undefined)
            .map(sceneObject => ({
                sceneObject,
                baselineData: sceneObject.data.clone(),
                // getWorldMatrix() hands back the live matrixWorld, so it has to be copied before
                // anything in the scene graph updates underneath us.
                parentWorldMatrix: sceneObject.getWorldMatrix().clone()
            }));
    }

    getCursorName(): string {
        return 'default';
    }

    start() {
        super.start();
        this.addToStack();
        this.connectMouse();

        if (this.#targets.length === 0) {
            console.warn('MoveSceneObjectsCmd: no scene objects to move');
            this.end();
            return;
        }

        this.#gizmo = new MoveGizmoHelper(this.canvasInputListener, this.modelRotationOffset);
        this.#gizmo.addToScene(SceneManager.scene);
        this.#gizmo.setOrigin(this.#computeSelectionCenter());
        this.#gizmo.enable();
        this.#updateGizmoVisibility();

        emitter.on('cameraRest', this.#onCameraRest);
        this.#enterIdle();
    }

    // Which parts are worth showing depends on the view angle, so they're re-evaluated once the
    // camera settles — but not mid-drag, where hiding the part being dragged would be jarring.
    #onCameraRest = () => {
        if (!this.#gizmo || this.#dragPart !== undefined) return;

        const viewDirection = this.#getCameraWorldDirection();
        if (this.#gizmoViewDirection && viewDirection.angleTo(this.#gizmoViewDirection) < tolerances.directionAngle)
            return;

        this.#updateGizmoVisibility();
        // The part that was hovered may have just been hidden, so hover is re-derived from where
        // the pointer actually is rather than left pointing at something the user can't see.
        this.canvasInputListener.notifyPointerMoveAtCurrentPosition();
    };

    #updateGizmoVisibility(): void {
        if (!this.#gizmo) return;

        const viewDirection = this.#getCameraWorldDirection();
        this.#gizmoViewDirection = viewDirection;
        this.#gizmo.setHovered(undefined);
        this.#gizmo.setVisiblePartsForView(viewDirection);
    }

    // getCameraWorldDirection() throws when no camera is set yet. A zero vector reads as degenerate
    // to the visibility predicates, which keeps every part rather than blanking out the gizmo.
    #getCameraWorldDirection(): THREE.Vector3 {
        try {
            return this.canvasInputListener.getCameraWorldDirection();
        } catch {
            return new THREE.Vector3();
        }
    }

    // A compound geometry keeps its 2D and 3D looks in the scene at the same time and lets camera
    // layers decide which one shows. Boxing the whole group would union both: something drawn on a
    // wall would get a pivot halfway down to its floor-plan projection at z=0. So only geometry
    // on a layer the active camera renders counts towards the centre.
    #computeSelectionCenter(): THREE.Vector3 {
        const cameraLayers = this.#getCameraLayers();
        const bounds = new THREE.Box3();
        for (const target of this.#targets) {
            const group = target.sceneObject.geometry.group;
            // The parent product's transform has to be brought up to date first, or the box lands
            // in the wrong place.
            group.updateWorldMatrix(true, true);

            if (!cameraLayers) {
                bounds.union(new THREE.Box3().setFromObject(group));
                continue;
            }

            group.traverseVisible(object => {
                const geometry = (object as THREE.Mesh).geometry as THREE.BufferGeometry | undefined;
                if (!geometry || !object.layers.test(cameraLayers)) return;

                if (!geometry.boundingBox) geometry.computeBoundingBox();
                if (geometry.boundingBox)
                    bounds.union(geometry.boundingBox.clone().applyMatrix4(object.matrixWorld));
            });
        }

        return bounds.isEmpty() ? new THREE.Vector3() : bounds.getCenter(new THREE.Vector3());
    }

    // getCameraLayers() throws when no camera is set yet; undefined falls back to the whole group.
    #getCameraLayers(): THREE.Layers | undefined {
        try {
            return this.canvasInputListener.getCameraLayers();
        } catch {
            return undefined;
        }
    }

    #enterIdle(): void {
        this.#dragPart = undefined;
        this.#dragStartOrigin = undefined;
        this.#dragAnchor = undefined;

        useCurrentCommandState.getState().clearMeasureText();
        // Nothing is moving between drags, so there is no direction left to draw.
        this.disableReferenceLine();
        this.showInfoMessage('command.moveSceneObjects.prompt');

        this.canvasInputListener.setRaycastMode(CanvasInputListenerModeEnum.RAYCAST_TARGET_OBJECTS);
        // Only the gizmo is pickable while idle. Raycasting ignores depthTest, so leaving the model
        // in the target list would let geometry standing in front of the gizmo steal clicks aimed
        // at an arrow; it also reduces "click outside the gizmo to finish" to an empty raycast.
        this.canvasInputListener.setTargetObjects(this.#gizmo?.getPartGroups() ?? []);
    }

    pointerMove(raycastResult: RaycastIntersection | undefined): boolean {
        if (!this.#gizmo) return false;

        return this.#dragPart === undefined
            ? this.#updateHover(raycastResult)
            : this.#updateDrag(raycastResult);
    }

    #updateHover(raycastResult: RaycastIntersection | undefined): boolean {
        const part = this.#gizmo?.getPartFromObject3D(raycastResult?.object ?? null);
        this.#gizmo?.setHovered(part);
        this.setCursor(part !== undefined ? 'move' : this.getCursorName());

        return part !== undefined;
    }

    #updateDrag(raycastResult: RaycastIntersection | undefined): boolean {
        if (!raycastResult || this.#dragPart === undefined || !this.#dragStartOrigin || !this.#gizmo)
            return false;

        // The click that started the drag landed on the gizmo's surface, not on the drag plane, so
        // the anchor is taken from the first sample on the plane itself. Anchoring on the gizmo hit
        // instead would make the selection jump by the gap between the two.
        if (!this.#dragAnchor) {
            this.#dragAnchor = raycastResult.point.clone();
            return true;
        }

        const worldDelta = raycastResult.point.clone().sub(this.#dragAnchor);

        if (this.#gizmo.isAxisPart(this.#dragPart)) {
            // The arrow's own line is the constraint, so there is nothing to snap to — and its
            // reference line is drawn for the whole drag by #setupDragRefAxes rather than being
            // gated on a snap threshold.
            this.#applyDelta(constrainDeltaToAxis(worldDelta, this.#gizmo.getWorldDirection(this.#dragPart)));
            return true;
        }

        // A planar drag can align with either of the plane's two axes. Those lines run through the
        // point the move started from, so it is the selection's own pivot that gets snapped onto
        // them — not the cursor, which sits wherever on the quad the user happened to click.
        // getRefLineAlignedPoint also lights up whichever line the pivot ends up riding.
        const movedOrigin = this.#dragStartOrigin.clone().add(worldDelta);
        this.#applyDelta(this.getRefLineAlignedPoint(movedOrigin).sub(this.#dragStartOrigin));
        return true;
    }

    #applyDelta(worldDelta: THREE.Vector3): void {
        for (const target of this.#targets) {
            if (!target.preview) continue;

            // The preview's group carries its parent's world transform (clone(true) baked it in),
            // while its data stays in parent-local coordinates — so the delta is applied locally,
            // exactly as GeometryMoveEditCmd does for a handle drag.
            const localDelta = worldDeltaToLocal(worldDelta, target.parentWorldMatrix);
            const previewData = target.baselineData.clone();
            previewData.applyMatrix4(translationMatrix(localDelta));

            target.preview.data = previewData;
            target.preview.geometry.update(previewData, true);
        }

        if (this.#dragStartOrigin)
            this.#gizmo?.setOrigin(this.#dragStartOrigin.clone().add(worldDelta));

        useCurrentCommandState.getState().setMeasureText(formatCm(worldDelta.length()));
    }

    point(raycastResult: RaycastIntersection | undefined): boolean {
        if (!this.#gizmo) return false;

        if (this.#dragPart !== undefined) {
            this.#commitDrag();
            return true;
        }

        const part = this.#gizmo.getPartFromObject3D(raycastResult?.object ?? null);
        if (part === undefined) {
            // A click anywhere off the gizmo finishes the command, alongside the X button.
            this.end();
            return true;
        }

        this.#startDrag(part);
        return true;
    }

    #startDrag(part: MoveGizmoPartEnum): void {
        if (!this.#gizmo) return;

        const origin = this.#gizmo.getOrigin();
        this.#dragPart = part;
        this.#dragStartOrigin = origin.clone();
        this.#dragAnchor = undefined;

        if (this.#gizmo.isAxisPart(part)) {
            // An axis on its own defines no pick surface, so the pointer is tracked against a
            // camera-facing plane and projected onto the axis afterwards.
            this.canvasInputListener.setTargetPlaneFromCamera(origin);
        } else {
            const normal = this.#gizmo.getWorldDirection(part);
            this.canvasInputListener.setTargetPlane(
                new THREE.Plane().setFromNormalAndCoplanarPoint(normal, origin));
        }
        this.canvasInputListener.setRaycastMode(CanvasInputListenerModeEnum.RAYCAST_PLANE);
        this.#setupDragRefAxes(part, origin);

        for (const target of this.#targets) {
            target.baselineData = target.sceneObject.data.clone();

            const preview = target.sceneObject.clone(true);
            preview.geometry.addToScene();
            preview.geometry.setStyle(getSceneObjectStyle(SceneObjectStyleEnum.Editing));
            preview.onAnnotationDirectionChange(this.modelRotationOffset);
            target.preview = preview;

            target.sceneObject.geometry.hide();
        }

        this.showInfoMessage('command.moveSceneObjects.promptDragging');
        useCurrentCommandState.getState().setMeasureText(formatCm(0));
    }

    // Reference lines are anchored at the point the move starts from, and mean different things
    // per part. An arrow gets one line along its own direction, shown for the whole drag: it draws
    // where the object is travelling, and there is nothing to snap to because the drag is already
    // confined to that line. A quad gets the two directions it spans, which a planar move can be
    // pulled onto — and their crossing back at the start point snaps the move to zero, giving the
    // user a way to back out of a drag before committing.
    #setupDragRefAxes(part: MoveGizmoPartEnum, origin: THREE.Vector3): void {
        if (!this.#gizmo) return;

        const isAxisDrag = this.#gizmo.isAxisPart(part);
        const directions = isAxisDrag
            ? [this.#gizmo.getWorldDirection(part)]
            : this.#gizmo.getWorldPlaneAxes(part);

        // Enable before setAxes, not after: setAxes only builds each line's geometry when the
        // helper is already enabled, so the other order leaves the group visible but empty.
        this.enableReferenceLine();
        this.refAxisHelper.setAxes(directions.map(direction => ({ origin: origin.clone(), direction })));

        if (isAxisDrag) this.refAxisHelper.showAllAxes();
    }

    #commitDrag(): void {
        for (const target of this.#targets) {
            if (!target.preview) continue;

            const movedData = target.preview.data.clone();
            // clone(true) detaches the preview from its parent product and drops the parent id; the
            // original still belongs to that product, so the id has to survive the round trip or the
            // object would be persisted as unparented.
            movedData.parentId = target.baselineData.parentId;

            target.sceneObject.data = movedData;
            target.sceneObject.geometry.update(movedData, true);
            target.sceneObject.geometry.show();

            this.updateEntity(target.sceneObject).then(success => {
                if (!success) console.error('MoveSceneObjectsCmd: failed to persist moved scene object');
            });
            emitter.emit('editCommandCommit', { ownerUuid: target.sceneObject.data.uuid });
        }

        this.#disposePreviews();
        // Re-derive the pivot from where the objects actually ended up, so chained moves can't drift
        // away from the selection.
        this.#gizmo?.setOrigin(this.#computeSelectionCenter());
        this.#enterIdle();
        this.canvasInputListener.notifyPointerMoveAtCurrentPosition();
    }

    // Rolls back an in-progress drag. The originals were never mutated — only hidden — so showing
    // them again and dropping the previews is the whole of it.
    #abortDrag(): void {
        if (this.#dragPart === undefined) return;

        this.#disposePreviews();
        for (const target of this.#targets) {
            target.sceneObject.geometry.show();
        }

        if (this.#gizmo && this.#dragStartOrigin)
            this.#gizmo.setOrigin(this.#dragStartOrigin);

        this.#enterIdle();
    }

    #disposePreviews(): void {
        for (const target of this.#targets) {
            if (!target.preview) continue;

            target.preview.geometry.removeFromScene();
            target.preview.dispose();
            target.preview = undefined;
        }
    }

    cancel(): void {
        if (this.#dragPart !== undefined) this.#abortDrag();
        else this.end();
    }

    // The gizmo defines the drag plane, so the plane-grid cycling right-click normally offers would
    // have nothing to act on.
    onSwitchPlane(): void {}

    end() {
        emitter.off('cameraRest', this.#onCameraRest);

        this.#abortDrag();
        this.#disposePreviews();
        for (const target of this.#targets) {
            target.sceneObject.geometry.show();
        }

        if (this.#gizmo) {
            this.#gizmo.disable();
            this.#gizmo.dispose();
            this.#gizmo = undefined;
        }

        this.canvasInputListener.setRaycastMode(CanvasInputListenerModeEnum.RAYCAST_TARGET_OBJECTS);
        this.canvasInputListener.resetTargetObjects();

        super.end();
        this.removeFromStack();
    }
}
